import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import VoiceDictate from "@/components/VoiceDictate";

export default function ClientForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postal_code: "",
    notes: "",
    status: "prospect",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isEdit) {
      (async () => {
        try {
          const { data } = await api.get(`/clients/${id}`);
          setForm({
            company_name: data.company_name || "",
            contact_name: data.contact_name || "",
            phone: data.phone || "",
            email: data.email || "",
            address: data.address || "",
            city: data.city || "",
            postal_code: data.postal_code || "",
            notes: data.notes || "",
            status: data.status || "prospect",
          });
        } catch (e) {
          setError("Impossible de charger ce client.");
        }
      })();
    }
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let res;
      if (isEdit) {
        res = await api.patch(`/clients/${id}`, form);
      } else {
        res = await api.post("/clients", form);
      }
      navigate(`/clients/${res.data.id}`);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer définitivement ce client et toutes ses visites ?")) return;
    try {
      await api.delete(`/clients/${id}`);
      navigate("/clients");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-3">
        {isEdit && (
          <button
            type="button"
            onClick={remove}
            data-testid="delete-client-btn"
            className="inline-flex items-center gap-2 text-rose-600 font-bold hover:text-rose-700 text-sm"
          >
            <Trash2 className="w-4 h-4" strokeWidth={2.5} /> Supprimer
          </button>
        )}
      </div>

      <h1 className="font-heading font-black text-3xl tracking-tight">
        {isEdit ? "Modifier le client" : "Nouveau client"}
      </h1>

      <form onSubmit={submit} className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 space-y-4 shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
        <Field label="Société *" testid="client-company" required value={form.company_name} onChange={(v) => update("company_name", v)} placeholder="Ex: Recyclo SAS" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Contact" testid="client-contact" value={form.contact_name} onChange={(v) => update("contact_name", v)} placeholder="Nom du contact" />
          <Field label="Téléphone" testid="client-phone" value={form.phone} onChange={(v) => update("phone", v)} placeholder="06 12 34 56 78" />
        </div>
        <Field label="Email" testid="client-email" type="email" value={form.email} onChange={(v) => update("email", v)} placeholder="contact@societe.fr" />
        <Field label="Adresse" testid="client-address" value={form.address} onChange={(v) => update("address", v)} placeholder="12 rue de l'industrie" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Code postal" testid="client-postal" value={form.postal_code} onChange={(v) => update("postal_code", v)} placeholder="75001" />
          <Field label="Ville" testid="client-city" value={form.city} onChange={(v) => update("city", v)} placeholder="Paris" />
        </div>

        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Statut</label>
          <div className="flex flex-wrap gap-2">
            {[
              ["prospect", "Prospect"],
              ["negociation", "Négociation"],
              ["signe", "Signé"],
              ["refuse", "Refusé"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`status-${k}`}
                onClick={() => update("status", k)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all active:scale-95 ${
                  form.status === k
                    ? "bg-stone-900 text-white border-stone-900"
                    : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-stone-700 mb-2">Notes</label>
          <div className="relative">
            <textarea
              data-testid="client-notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={4}
              placeholder="Infos utiles, accès, horaires d'ouverture..."
              className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl px-4 py-3 pr-14 transition-all"
            />
            <div className="absolute bottom-3 right-3">
              <VoiceDictate
                compact
                onTranscript={(text) =>
                  update("notes", form.notes ? form.notes + " " + text : text)
                }
              />
            </div>
          </div>
        </div>

        {error && (
          <div data-testid="form-error" className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-2xl px-4 py-3">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="client-submit"
          className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 active:scale-[0.98]"
        >
          <Save className="w-4 h-4" strokeWidth={3} /> {busy ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer la fiche"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, testid, required, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-bold text-stone-700 mb-2">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl px-4 py-3 transition-all font-medium"
      />
    </div>
  );
}
